// ListScreen.jsx - Livreur Planning
import React, { useState, useEffect, useMemo } from "react";
import { Container, Row, Col, Badge, Button } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import DynamicTable from "../../components/DynamicTable";
import SearchBar from "../../components/SearchBar";
import ClickBack from "../../components/buttons/ClickBack";
import { fetchAllLivreurs } from "./dataService";
import IconEdit from "../../images/edit.png";
import IconShow from "../../images/fi_eye.png";

function ListScreen() {
  const [livreursData, setLivreursData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState(null);
  const history = useHistory();

  // Fetch data on component mount
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchAllLivreurs();
      console.log("API Response:", data); // Debug log

      // Handle different response structures
      if (data && data.data) {
        // Check if data.data is an array or contains a data property (paginated response)
        if (Array.isArray(data.data)) {
          setLivreursData(data.data);
        } else if (data.data.data && Array.isArray(data.data.data)) {
          setLivreursData(data.data.data);
        } else {
          setLivreursData([]);
        }
      } else if (Array.isArray(data)) {
        setLivreursData(data);
      } else {
        console.warn("Unexpected data structure:", data);
        setLivreursData([]);
      }
    } catch (error) {
      console.error("Error fetching livreurs:", error);
      setError(error.response?.data?.message || error.message || "Erreur lors du chargement des livreurs");
    } finally {
      setLoading(false);
    }
  };

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!searchTerm) return livreursData;

    return livreursData.filter((livreur) => {
      const fullName = `${livreur.nom} ${livreur.prenom}`.toLowerCase();
      const email = (livreur.email || "").toLowerCase();
      return (
        fullName.includes(searchTerm.toLowerCase()) ||
        email.includes(searchTerm.toLowerCase())
      );
    });
  }, [livreursData, searchTerm]);

  // Define table columns
  const columns = useMemo(
    () => [
      {
        Header: "ID",
        accessor: "id",
      },
      {
        Header: "Nom",
        accessor: "nom",
      },
      {
        Header: "Prénom",
        accessor: "prenom",
      },
      {
        Header: "Email",
        accessor: "email",
      },
      {
        Header: "Téléphone",
        accessor: "num_tel",
        Cell: ({ value }) => value || "-",
      },
      {
        Header: "Statut",
        accessor: "status",
        Cell: ({ value }) => (
          <Badge bg={value === 1 ? "success" : "danger"}>
            {value === 1 ? "Actif" : "Inactif"}
          </Badge>
        ),
      },
      {
        Header: "Actions",
        accessor: "actions",
        Cell: ({ row }) => (
          <div className="d-flex gap-2">
            <Button
              variant="link"
              className="p-0"
              onClick={() => history.push(`/livreurs/planning/${row.original.id}/view`)}
              title="Voir le planning"
            >
              <img src={IconShow} alt="Voir" width="20" />
            </Button>
            <Button
              variant="link"
              className="p-0"
              onClick={() => history.push(`/livreurs/planning/${row.original.id}/edit`)}
              title="Modifier le planning"
            >
              <img src={IconEdit} alt="Modifier" width="20" />
            </Button>
          </div>
        ),
      },
    ],
    [history]
  );

  return (
    <Container fluid className="p-4">
      <Row className="mb-4">
        <Col>
          <ClickBack />
          <h2 className="mt-3">Plannings des livreurs</h2>
          <p className="text-muted">
            Gérez les horaires de travail, congés et disponibilités de vos livreurs
          </p>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Rechercher un livreur (nom, prénom, email)..."
          />
        </Col>
        <Col md={6} className="text-end">
          <Badge bg="info" className="me-2">
            Total: {filteredData.length}
          </Badge>
        </Col>
      </Row>

      <Row>
        <Col>
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Chargement...</span>
              </div>
            </div>
          ) : error ? (
            <div className="alert alert-danger" role="alert">
              <h5 className="alert-heading">Erreur</h5>
              <p>{error}</p>
              <hr />
              <p className="mb-0">
                Vérifiez que l'API backend est accessible et que l'endpoint <code>/users/role/3</code> fonctionne correctement.
              </p>
              <button className="btn btn-primary mt-3" onClick={fetchData}>
                Réessayer
              </button>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="alert alert-info" role="alert">
              <h5 className="alert-heading">Aucun livreur trouvé</h5>
              <p>
                {searchTerm
                  ? "Aucun livreur ne correspond à votre recherche."
                  : "Aucun livreur n'est enregistré dans le système pour le moment."}
              </p>
              <hr />
              <p className="mb-0">
                <strong>Vérifications possibles :</strong>
              </p>
              <ul>
                <li>L'endpoint <code>/users/role/3</code> renvoie-t-il des données ?</li>
                <li>Des utilisateurs avec le rôle "Livreur" (role=3) existent-ils dans la base de données ?</li>
                <li>Le backend est-il correctement configuré en production ?</li>
              </ul>
              <button className="btn btn-primary" onClick={fetchData}>
                Actualiser
              </button>
            </div>
          ) : (
            <DynamicTable columns={columns} data={filteredData} />
          )}
        </Col>
      </Row>
    </Container>
  );
}

export default ListScreen;
